import * as React from "react";
import type { Column, Registrant } from "../api.js";
import type { SortSpec } from "../filter.js";
import { Cell } from "./Cell.js";
import { Icon } from "./Icon.js";

export function DataTable({
  rows,
  columns,
  visibleIds,
  sort,
  onSort,
  onRowClick,
  selected,
  setSelected,
}: {
  rows: Registrant[];
  columns: Column[];
  visibleIds: string[];
  sort: SortSpec | null;
  onSort: (s: SortSpec | null) => void;
  onRowClick: (r: Registrant) => void;
  selected: Set<string>;
  setSelected: (s: Set<string>) => void;
}) {
  const visibleCols = visibleIds
    .map((id) => columns.find((c) => c.id === id))
    .filter((c): c is Column => Boolean(c));

  function clickHeader(col: Column) {
    if (col.sortable === false) return;
    if (!sort || sort.columnId !== col.id) onSort({ columnId: col.id, dir: "asc" });
    else if (sort.dir === "asc") onSort({ columnId: col.id, dir: "desc" });
    else onSort(null);
  }

  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const someSelected = rows.some((r) => selected.has(r.id)) && !allSelected;

  function toggleAll() {
    if (allSelected) {
      setSelected(new Set());
      return;
    }
    const next = new Set(selected);
    for (const r of rows) next.add(r.id);
    setSelected(next);
  }

  function toggleOne(id: string) {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id);
    else s.add(id);
    setSelected(s);
  }

  function colWidth(col: Column): number {
    switch (col.id) {
      case "fullName": return 220;
      case "email": return 240;
      case "phone": return 150;
      case "eventName": return 200;
      case "ticketType": return 140;
      case "ticketCode": return 130;
      case "registeredAt": return 160;
      case "amountPaid":
      case "amountOwing": return 110;
      case "membershipLevel": return 140;
      case "gender": return 100;
      case "dateOfBirth": return 130;
      default: return 160;
    }
  }

  return (
    <div className="data-table">
      <div className="table-scroll">
        <table>
          <colgroup>
            <col style={{ width: 44 }} />
            {visibleCols.map((c) => <col key={c.id} style={{ width: colWidth(c) }} />)}
          </colgroup>
          <thead>
            <tr>
              <th className="cell-check sticky-l">
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={allSelected}
                  ref={(el) => { if (el) el.indeterminate = someSelected; }}
                  onChange={toggleAll}
                />
              </th>
              {visibleCols.map((col) => {
                const align = col.type === "currency" || col.type === "number" ? "right" : "";
                return (
                  <th
                    key={col.id}
                    className={align}
                    onClick={() => clickHeader(col)}
                    title={col.sortable === false ? "" : "Sort"}
                  >
                    <span className="th-inner">
                      {col.label}
                      {sort?.columnId === col.id && (
                        <Icon name={sort.dir === "asc" ? "arrowUp" : "arrowDown"} size={12} />
                      )}
                    </span>
                  </th>
                );
              })}
              <th className="th-spacer" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td className="empty" colSpan={visibleCols.length + 2}>
                  <div className="empty-inner">
                    <Icon name="search" size={28} />
                    <h3>No registrants match these filters</h3>
                    <p>Try clearing a filter or widening your search.</p>
                  </div>
                </td>
              </tr>
            ) : rows.map((r) => (
              <tr
                key={r.id}
                className={selected.has(r.id) ? "row-selected" : ""}
                onClick={() => onRowClick(r)}
              >
                <td className="cell-check sticky-l" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    className="checkbox"
                    checked={selected.has(r.id)}
                    onChange={() => toggleOne(r.id)}
                  />
                </td>
                {visibleCols.map((col) => {
                  const align = col.type === "currency" || col.type === "number" ? "right" : "";
                  return (
                    <td key={col.id} className={align}>
                      <Cell row={r} column={col} />
                    </td>
                  );
                })}
                <td className="cell-spacer" />
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <style>{`
        .data-table {
          flex: 1;
          background: white;
          border: 1px solid var(--border);
          border-radius: var(--r-lg);
          overflow: hidden;
          display: flex; flex-direction: column;
          box-shadow: var(--shadow-xs);
          min-height: 0;
        }
        .data-table .table-scroll { overflow: auto; flex: 1; }
        .data-table table { border-collapse: separate; border-spacing: 0; width: 100%; font-size: 13px; }
        .data-table thead th {
          position: sticky; top: 0; z-index: 3;
          background: var(--ink-50);
          color: var(--ink-600);
          font-weight: 600;
          font-size: 12px;
          letter-spacing: 0.01em;
          text-align: left;
          padding: 0 12px;
          height: 38px;
          border-bottom: 1px solid var(--border);
          cursor: pointer;
          user-select: none;
          white-space: nowrap;
        }
        .data-table thead th.right { text-align: right; }
        .data-table thead th.right .th-inner { justify-content: flex-end; }
        .data-table thead th:hover { background: var(--ink-100); color: var(--ink-800); }
        .data-table .th-inner { display: inline-flex; align-items: center; gap: 6px; }
        .data-table .th-spacer, .data-table .cell-spacer { padding: 0; width: auto; min-width: 24px; }
        .data-table .cell-check { width: 44px; padding: 0 0 0 16px; cursor: default; }
        .data-table thead .cell-check { background: var(--ink-50); }
        .data-table tbody td {
          padding: 0 12px;
          height: var(--row-h);
          border-bottom: 1px solid var(--ink-100);
          color: var(--ink-800);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 0;
          vertical-align: middle;
        }
        .data-table tbody td.right { text-align: right; font-variant-numeric: tabular-nums; }
        .data-table tbody tr { transition: background 80ms; cursor: pointer; }
        .data-table tbody tr:hover { background: var(--ink-50); }
        .data-table tbody tr.row-selected { background: var(--brand-50); }
        .data-table tbody tr.row-selected:hover { background: #e3f1f9; }
        .data-table .sticky-l { position: sticky; left: 0; z-index: 2; background: white; }
        .data-table thead .sticky-l { background: var(--ink-50); z-index: 4; }
        .data-table tbody tr:hover .sticky-l { background: var(--ink-50); }
        .data-table tbody tr.row-selected .sticky-l { background: var(--brand-50); }

        .data-table .empty { padding: 60px 20px; text-align: center; }
        .data-table .empty-inner { color: var(--ink-500); }
        .data-table .empty-inner svg { color: var(--ink-300); margin-bottom: 12px; }
        .data-table .empty-inner h3 { font-size: 16px; margin: 0 0 6px; color: var(--ink-700); }
        .data-table .empty-inner p { margin: 0; font-size: 13px; }

        .data-table .cell-name { display: flex; align-items: center; gap: 10px; }
        .data-table .cell-name .nm { font-weight: 600; color: var(--ink-900); overflow: hidden; text-overflow: ellipsis; }
        .data-table .cell-tags { display: flex; gap: 4px; flex-wrap: nowrap; overflow: hidden; }
        .data-table .cell-tags .chip { height: 22px; padding: 0 8px; font-size: 11px; }
        .data-table .cell-bool-yes { color: var(--green-500); }
        .data-table .cell-bool-no  { color: var(--ink-300); }
        .data-table .cell-gender-F { color: #c93571; }
        .data-table .cell-gender-M { color: #1378a5; }
      `}</style>
    </div>
  );
}
